/**
 * Image Conversion, Optimization & AI Cover Studio Utility
 * Converts user/camera images and generates AI Cover Images in optimized WebP / JPEG format
 * with guaranteed size limit (< 250KB) for reliable persistence in Firestore & cloud databases.
 */

import { getAIImageGeneratorById } from '../constants/aiModels';

export interface WebPConversionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.0 to 1.0 (default 0.8)
  maxSizeBytes?: number; // default 250000 (~245KB for fast Firestore saving)
  preferredFormat?: 'image/webp' | 'image/jpeg';
}

export interface WebPConversionResult {
  file: File;
  dataUrl: string;
  width: number;
  height: number;
  originalSize: number;
  compressedSize: number;
}

export interface AICoverGenerationOptions {
  title: string;
  category?: string;
  generatorId?: string;
  visualStyle?: string;
  aspectRatio?: '16:9' | '1:1';
  subtitle?: string;
  priceJpy?: number;
  imageSearchPrompt?: string;
  discoveredImages?: string[];
}

export interface AICoverVariation {
  id: string;
  label: string;
  url: string;
  styleName: string;
}

export interface AICoverGenerationResult {
  primaryCoverUrl: string;
  variations: AICoverVariation[];
  optimizedPrompt: string;
  generatorName: string;
  officialSiteUrl?: string | null;
}

/**
 * Calculates byte size of a base64 Data URL string
 */
export function getDataUrlByteSize(dataUrl: string): number {
  if (!dataUrl || !dataUrl.includes(',')) return 0;
  const base64Str = dataUrl.split(',')[1] || '';
  const padding = (base64Str.match(/=/g) || []).length;
  return Math.max(0, Math.round((base64Str.length * 3) / 4 - padding));
}

/**
 * Formats byte size into human readable string (e.g. "124 KB")
 */
export function formatImageSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Converts a File, Blob, or existing Data URL to an optimized, compressed Data URL string.
 */
export function convertToWebPDataUrl(
  fileOrBlobOrUrl: File | Blob | string,
  options: WebPConversionOptions = {}
): Promise<string> {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.8,
    maxSizeBytes = 250000,
    preferredFormat = 'image/jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    if (typeof fileOrBlobOrUrl === 'string' && /^https?:\/\//i.test(fileOrBlobOrUrl)) {
      return resolve(fileOrBlobOrUrl);
    }

    const processImageElement = (img: HTMLImageElement) => {
      try {
        let currentWidth = img.naturalWidth || img.width;
        let currentHeight = img.naturalHeight || img.height;

        if (!currentWidth || !currentHeight) {
          return resolve(img.src);
        }

        let targetWidth = currentWidth;
        let targetHeight = currentHeight;

        if (targetWidth > maxWidth) {
          targetHeight = Math.round((targetHeight * maxWidth) / targetWidth);
          targetWidth = maxWidth;
        }

        if (targetHeight > maxHeight) {
          targetWidth = Math.round((targetWidth * maxHeight) / targetHeight);
          targetHeight = maxHeight;
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) {
          return resolve(img.src);
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        let formatToUse = preferredFormat;
        let currentQuality = quality;
        let outputDataUrl = canvas.toDataURL(formatToUse, currentQuality);

        if (formatToUse === 'image/webp' && !outputDataUrl.startsWith('data:image/webp')) {
          formatToUse = 'image/jpeg';
          outputDataUrl = canvas.toDataURL('image/jpeg', currentQuality);
        }

        let attempts = 0;
        let currentByteSize = getDataUrlByteSize(outputDataUrl);

        while (currentByteSize > maxSizeBytes && attempts < 4) {
          attempts++;
          currentQuality = Math.max(0.4, currentQuality - 0.15);

          if (attempts >= 2) {
            targetWidth = Math.round(targetWidth * 0.8);
            targetHeight = Math.round(targetHeight * 0.8);
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, targetWidth, targetHeight);
            ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
          }

          outputDataUrl = canvas.toDataURL(formatToUse, currentQuality);
          currentByteSize = getDataUrlByteSize(outputDataUrl);
        }

        resolve(outputDataUrl);
      } catch (err) {
        console.warn('[imageUtils] Fallback on compression error:', err);
        if (img.src) {
          resolve(img.src);
        } else {
          reject(err);
        }
      }
    };

    if (typeof fileOrBlobOrUrl === 'string') {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => processImageElement(img);
      img.onerror = () => resolve(fileOrBlobOrUrl);
      img.src = fileOrBlobOrUrl;
    } else {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Erro ao ler a foto da câmera ou arquivo.'));
      reader.onload = (e) => {
        const dataStr = e.target?.result as string;
        if (!dataStr) {
          return reject(new Error('Não foi possível ler a imagem.'));
        }
        const img = new Image();
        img.onload = () => processImageElement(img);
        img.onerror = () => reject(new Error('Formato de imagem não suportado.'));
        img.src = dataStr;
      };
      reader.readAsDataURL(fileOrBlobOrUrl);
    }
  });
}

/**
 * Converts a File object to an optimized File object.
 */
export function convertToWebPFile(
  file: File,
  options: WebPConversionOptions = {}
): Promise<File> {
  return new Promise((resolve, reject) => {
    convertToWebPDataUrl(file, options)
      .then((dataUrl) => {
        const arr = dataUrl.split(',');
        const mimeMatch = arr[0].match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }

        const blob = new Blob([u8arr], { type: mime });
        const ext = mime.includes('webp') ? 'webp' : 'jpg';
        const newFileName = file.name.substring(0, file.name.lastIndexOf('.')) || 'photo';
        const finalFileName = `${newFileName}.${ext}`;

        const finalFile = new File([blob], finalFileName, {
          type: mime,
          lastModified: Date.now(),
        });

        resolve(finalFile);
      })
      .catch((err) => reject(err));
  });
}

/**
 * Full conversion function returning metadata, file and Data URL
 */
export async function processImageToWebP(
  file: File,
  options: WebPConversionOptions = {}
): Promise<WebPConversionResult> {
  const dataUrl = await convertToWebPDataUrl(file, options);
  const webpFile = await convertToWebPFile(file, options);
  const byteSize = getDataUrlByteSize(dataUrl);

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        file: webpFile,
        dataUrl,
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
        originalSize: file.size,
        compressedSize: byteSize,
      });
    };
    img.src = dataUrl;
  });
}

/**
 * Curated high-resolution Japanese thematic photography pool with CORS support
 */
const THEMATIC_JAPAN_PHOTOS: Record<string, string[]> = {
  collectibles: [
    'https://images.unsplash.com/photo-1607604276583-eef5d076aa5f?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1613376023733-0a73315d9b06?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=1200&auto=format&fit=crop&q=80'
  ],
  games: [
    'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1612287230202-1ff1d85d1bdf?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1538481199705-c710c4e965fc?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=1200&auto=format&fit=crop&q=80'
  ],
  beauty: [
    'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1571781926291-c477ebfd024b?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=1200&auto=format&fit=crop&q=80'
  ],
  shipping: [
    'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=1200&auto=format&fit=crop&q=80'
  ],
  fashion: [
    'https://images.unsplash.com/photo-1503899036084-c55cdd92da26?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1552346154-21d32810aba3?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=1200&auto=format&fit=crop&q=80'
  ],
  snacks: [
    'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1515823064-d6e0c04616a7?w=1200&auto=format&fit=crop&q=80'
  ],
  tokyo: [
    'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1503899036084-c55cdd92da26?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1200&auto=format&fit=crop&q=80'
  ]
};

function selectThematicPhotos(topic: string, category?: string): string[] {
  const combined = `${topic} ${category || ''}`.toLowerCase();
  if (combined.includes('figure') || combined.includes('anime') || combined.includes('pokemon') || combined.includes('tcg') || combined.includes('colecion') || combined.includes('one piece') || combined.includes('goku')) {
    return THEMATIC_JAPAN_PHOTOS.collectibles;
  }
  if (combined.includes('game') || combined.includes('nintendo') || combined.includes('playstation') || combined.includes('console') || combined.includes('retro')) {
    return THEMATIC_JAPAN_PHOTOS.games;
  }
  if (combined.includes('beauty') || combined.includes('cosm') || combined.includes('pele') || combined.includes('biore') || combined.includes('shiseido') || combined.includes('sk-ii')) {
    return THEMATIC_JAPAN_PHOTOS.beauty;
  }
  if (combined.includes('frete') || combined.includes('ems') || combined.includes('log') || combined.includes('envio') || combined.includes('caixa') || combined.includes('alfandega')) {
    return THEMATIC_JAPAN_PHOTOS.shipping;
  }
  if (combined.includes('moda') || combined.includes('street') || combined.includes('roupa') || combined.includes('tenis') || combined.includes('bape') || combined.includes('uniqlo')) {
    return THEMATIC_JAPAN_PHOTOS.fashion;
  }
  if (combined.includes('doce') || combined.includes('snack') || combined.includes('matcha') || combined.includes('kitkat')) {
    return THEMATIC_JAPAN_PHOTOS.snacks;
  }
  return THEMATIC_JAPAN_PHOTOS.tokyo;
}

function loadImageWithTimeout(url: string, timeoutMs = 3500): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.referrerPolicy = 'no-referrer';
    const timer = setTimeout(() => {
      resolve(null);
    }, timeoutMs);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = url;
  });
}

function wrapCanvasText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number
): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
      if (lines.length >= maxLines - 1) {
        break;
      }
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine);
  }
  return lines;
}

/**
 * Renders a high-resolution custom AI Studio Cover onto an HTML5 Canvas and returns a compressed JPEG Data URL
 * Unrestricted & clean: uses only the actual topic/title and category without forced store watermarks.
 */
async function renderStudioCoverCanvas(params: {
  title: string;
  category: string;
  generatorId: string;
  variantIndex: number;
  aspectRatio: '16:9' | '1:1';
  bgPhotoUrl?: string;
  priceJpy?: number;
  cleanPhotoOnly?: boolean;
}): Promise<string> {
  const gen = getAIImageGeneratorById(params.generatorId);
  const width = params.aspectRatio === '1:1' ? 960 : 1200;
  const height = params.aspectRatio === '1:1' ? 960 : 630;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) return params.bgPhotoUrl || THEMATIC_JAPAN_PHOTOS.tokyo[0];

  // Palette shifts based on variantIndex so each variation looks distinct
  const palettes: Array<[string, string, string, string]> = [
    [gen.themeColors[0], gen.themeColors[1], gen.themeColors[2], gen.accentHex],
    ['#090d16', '#7f1d1d', '#dc2626', '#fbbf24'],
    ['#0f172a', '#1e1b4b', '#4f46e5', '#38bdf8'],
    ['#052e16', '#111827', '#059669', '#34d399']
  ];
  const [c1, c2, c3, accent] = palettes[params.variantIndex % palettes.length];

  // 1. Base Gradient Background
  const bgGrad = ctx.createLinearGradient(0, 0, width, height);
  bgGrad.addColorStop(0, c1);
  bgGrad.addColorStop(0.55, c2);
  bgGrad.addColorStop(1, c3);
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, width, height);

  // 2. Background Image (AI-generated or Official Site image)
  if (params.bgPhotoUrl) {
    const loadedImg = await loadImageWithTimeout(params.bgPhotoUrl, 6000);
    if (loadedImg) {
      try {
        ctx.save();
        ctx.globalAlpha = params.cleanPhotoOnly ? 1.0 : params.variantIndex === 0 ? 0.65 : 0.52;
        const imgRatio = loadedImg.width / loadedImg.height;
        const canvasRatio = width / height;
        let drawW = width;
        let drawH = height;
        let offsetX = 0;
        let offsetY = 0;
        if (imgRatio > canvasRatio) {
          drawW = height * imgRatio;
          offsetX = -(drawW - width) / 2;
        } else {
          drawH = width / imgRatio;
          offsetY = -(drawH - height) / 2;
        }
        ctx.drawImage(loadedImg, offsetX, offsetY, drawW, drawH);
        ctx.restore();

        if (params.cleanPhotoOnly) {
          return canvas.toDataURL('image/jpeg', 0.88);
        }

        // Subtle editorial vignette for headline legibility
        const overlayGrad = ctx.createLinearGradient(0, 0, width * 0.9, height);
        overlayGrad.addColorStop(0, 'rgba(6, 10, 22, 0.82)');
        overlayGrad.addColorStop(0.55, 'rgba(10, 15, 30, 0.58)');
        overlayGrad.addColorStop(1, 'rgba(15, 23, 42, 0.30)');
        ctx.fillStyle = overlayGrad;
        ctx.fillRect(0, 0, width, height);
      } catch {}
    }
  }

  // 3. Ambient Studio Glow
  ctx.save();
  const sunX = width * 0.82;
  const sunY = height * 0.48;
  const sunRadius = Math.min(width, height) * 0.34;

  const glowGrad = ctx.createRadialGradient(sunX, sunY, 10, sunX, sunY, sunRadius * 1.4);
  glowGrad.addColorStop(0, `${accent}44`);
  glowGrad.addColorStop(0.5, `${c3}22`);
  glowGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = glowGrad;
  ctx.beginPath();
  ctx.arc(sunX, sunY, sunRadius * 1.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // 4. Left Accent Bar
  const padX = Math.round(width * 0.065);
  const padY = Math.round(height * 0.11);
  ctx.fillStyle = accent;
  ctx.fillRect(padX, padY + 62, 6, height - padY * 2 - 90);

  // 5. Top Category Pill (only if category is provided)
  if (params.category) {
    ctx.save();
    const catText = params.category.toUpperCase();
    ctx.font = '800 17px sans-serif';
    const catWidth = ctx.measureText(catText).width + 36;

    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.roundRect(padX, padY, catWidth, 38, 10);
    ctx.fill();

    ctx.fillStyle = '#090d16';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(catText, padX + 18, padY + 19);
    ctx.restore();
  }

  // 6. Main Headline Title (exact user/AI title)
  const cleanHeadline = (params.title || '').trim();
  const titleFontSize = params.aspectRatio === '1:1' ? 50 : 52;
  ctx.font = `900 ${titleFontSize}px sans-serif`;
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  const maxTextW = width - padX * 2 - 40;
  const lines = wrapCanvasText(ctx, cleanHeadline, maxTextW, 4);
  const lineHeight = Math.round(titleFontSize * 1.2);
  const startY = padY + 76;

  lines.forEach((line, idx) => {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.70)';
    ctx.fillText(line, padX + 26, startY + idx * lineHeight + 3);
    ctx.fillStyle = idx === lines.length - 1 && lines.length > 1 ? accent : '#ffffff';
    ctx.fillText(line, padX + 24, startY + idx * lineHeight);
  });

  // 7. Optional Price Tag (only when explicitly passed for Store Products)
  if (params.priceJpy && params.priceJpy > 0) {
    const priceStr = `¥ ${params.priceJpy.toLocaleString('ja-JP')} JPY`;
    ctx.font = '800 24px sans-serif';
    const pw = ctx.measureText(priceStr).width + 40;
    const py = startY + lines.length * lineHeight + 22;
    ctx.fillStyle = 'rgba(230, 57, 70, 0.95)';
    ctx.beginPath();
    ctx.roundRect(padX + 24, py, pw, 46, 12);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'middle';
    ctx.fillText(priceStr, padX + 44, py + 23);
  }

  return canvas.toDataURL('image/jpeg', 0.86);
}

/**
 * Full Unrestricted AI Cover Studio Generator:
 * Generates topic-specific AI images & official website images without restrictions or watermarks.
 */
export async function generateAiCoverImageStudio(
  options: AICoverGenerationOptions
): Promise<AICoverGenerationResult> {
  const gen = getAIImageGeneratorById(options.generatorId || 'nano_banana');
  const cleanTitle = (options.title || 'Artigo em Destaque').trim();
  const cleanCategory = (options.category || '').trim();
  const aspectRatio = options.aspectRatio || '16:9';
  const styleLabel = options.visualStyle || gen.stylePreset;
  const searchPrompt = (options.imageSearchPrompt || cleanTitle).trim();

  const optimizedPrompt = `[${gen.name}] High-resolution ${aspectRatio} cover image specifically about "${searchPrompt}", ${styleLabel}, ${gen.promptSuffix} --ar ${aspectRatio}`;

  const fallbackPhotos = selectThematicPhotos(cleanTitle, cleanCategory);

  // 1. Call server-side AI Cover & Official Website Image Discovery endpoint
  let serverPrimaryUrl: string | null = null;
  let serverAiUrls: string[] = [];
  let discoveredWebImages: string[] = options.discoveredImages ? [...options.discoveredImages] : [];
  let officialSiteUrl: string | null = null;

  try {
    const res = await fetch('/api/generate-cover-image', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: searchPrompt,
        category: cleanCategory,
        generatorId: gen.id,
        visualStyle: styleLabel,
        aspectRatio
      })
    });
    if (res.ok) {
      const data = await res.json();
      if (data?.success && data?.data) {
        if (data.data.imageUrl) {
          serverPrimaryUrl = data.data.imageUrl;
        }
        if (Array.isArray(data.data.aiImageUrls)) {
          serverAiUrls = data.data.aiImageUrls;
        }
        if (Array.isArray(data.data.discoveredWebImages)) {
          discoveredWebImages = Array.from(new Set([...discoveredWebImages, ...data.data.discoveredWebImages]));
        }
        if (data.data.officialSiteUrl) {
          officialSiteUrl = data.data.officialSiteUrl;
        }
      }
    }
  } catch {
    // Fallback to client-side generative URLs and Canvas rendering
  }

  // Ensure we always have direct generative AI image URLs for the exact topic
  if (serverAiUrls.length === 0) {
    const w = aspectRatio === '1:1' ? 1024 : 1280;
    const h = aspectRatio === '1:1' ? 1024 : 720;
    const seed = Math.floor(Math.random() * 899999) + 10000;
    serverAiUrls = [
      `https://image.pollinations.ai/prompt/${encodeURIComponent(`${searchPrompt}, ${styleLabel}, high resolution, 8k, no watermark`)}?width=${w}&height=${h}&nologo=true&seed=${seed}&model=flux`,
      `https://image.pollinations.ai/prompt/${encodeURIComponent(`${searchPrompt}, editorial photography, ${gen.promptSuffix}, 8k`)}?width=${w}&height=${h}&nologo=true&seed=${seed + 29}&model=flux`
    ];
  }

  const bestPhotoBg =
    serverPrimaryUrl ||
    discoveredWebImages[0] ||
    serverAiUrls[0] ||
    fallbackPhotos[0];

  const secondPhotoBg =
    discoveredWebImages[1] ||
    serverAiUrls[1] ||
    discoveredWebImages[0] ||
    fallbackPhotos[1];

  // 2. Render clean Editorial Magazine Covers blending the topic's real/AI image with the exact headline
  const [editorialCover1, editorialCover2] = await Promise.all([
    renderStudioCoverCanvas({
      title: cleanTitle,
      category: cleanCategory,
      generatorId: gen.id,
      variantIndex: 0,
      aspectRatio,
      bgPhotoUrl: bestPhotoBg,
      priceJpy: options.priceJpy
    }),
    renderStudioCoverCanvas({
      title: cleanTitle,
      category: cleanCategory,
      generatorId: gen.id,
      variantIndex: 1,
      aspectRatio,
      bgPhotoUrl: secondPhotoBg,
      priceJpy: options.priceJpy
    })
  ]);

  const variations: AICoverVariation[] = [];

  // Variation 1: Pure AI Image (No text overlay) or Server-generated Base64 Image
  variations.push({
    id: `var-ai-pure-${Date.now()}`,
    label: `${gen.icon} Arte IA Pura (Sem Texto)`,
    url: serverPrimaryUrl || serverAiUrls[0],
    styleName: `${gen.name.split(' (')[0]} • Imagem Limpa`
  });

  // Variation 2: Real Image from Official Company Website / Web Search (if discovered) OR 2nd AI Generation
  if (discoveredWebImages.length > 0) {
    variations.push({
      id: `var-web-official-${Date.now()}`,
      label: `🌐 Imagem Oficial Encontrada na Web`,
      url: discoveredWebImages[0],
      styleName: officialSiteUrl ? 'Extraída do Site Oficial' : 'Pesquisa Web Real'
    });
  } else if (serverAiUrls[1]) {
    variations.push({
      id: `var-ai-alt-${Date.now()}`,
      label: `✨ Variação Criativa IA 2`,
      url: serverAiUrls[1],
      styleName: 'Ângulo Alternativo IA'
    });
  }

  // Variation 3: Clean Editorial Cover with Title (No store watermarks)
  variations.push({
    id: `var-editorial-1-${Date.now()}`,
    label: `📰 Capa Editorial com Título`,
    url: editorialCover1,
    styleName: 'Revista / Blog Editorial'
  });

  // Variation 4: Second Official Image or Alternative Editorial Cover
  if (discoveredWebImages.length > 1) {
    variations.push({
      id: `var-web-2-${Date.now()}`,
      label: `📸 Foto Oficial 2 (Site / Web)`,
      url: discoveredWebImages[1],
      styleName: 'Pesquisa Oficial'
    });
  } else {
    variations.push({
      id: `var-editorial-2-${Date.now()}`,
      label: `🎨 Capa Destaque Alternativa`,
      url: editorialCover2,
      styleName: styleLabel
    });
  }

  return {
    primaryCoverUrl: serverPrimaryUrl || discoveredWebImages[0] || editorialCover1,
    variations: variations.slice(0, 4),
    optimizedPrompt,
    generatorName: gen.name,
    officialSiteUrl
  };
}
